/**
 * Aplicación de tablero: conecta documento, lienzo, herramientas e interfaz.
 * Implementa el contexto que reciben las herramientas (AppContext).
 */
import * as Y from 'yjs'
import type { BackgroundId, BoardKind, Presence, Shape, ShapeId } from '../shared/types.ts'
import { presenceColorFor } from '../shared/palette.ts'
import { Presentation } from './present.ts'
import {
  addChild,
  addFloating,
  addRelation,
  addSibling,
  deleteSubtree,
  duplicateSubtree,
  relayout,
  seedMindMap,
  toggleCollapse,
} from './mindmap/actions.ts'
import { subtreeIds } from './mindmap/layout.ts'
import { PresentBar } from './ui/present-bar.ts'
import { api } from './api.ts'
import { preloadImages, setImageLoadedCallback } from './canvas/images.ts'
import { Renderer } from './canvas/renderer.ts'
import { forgetShapeCache } from './canvas/render-shape.ts'
import { hitTest } from './canvas/hit.ts'
import { InputManager, isEditingField } from './input.ts'
import { connectNativeBridge, isNativeShell } from './native/bridge.ts'
import { clamp, type Box } from './lib/geom.ts'
import { BoardDoc } from './state/board.ts'
import { WebsocketProvider } from './state/provider.ts'
import { MAX_ZOOM, MIN_ZOOM, Store, type ToolId } from './state/store.ts'
import {
  createImage,
  createNote,
  createText,
  duplicateShapes,
  newId,
  shapeBounds,
} from './state/shapes.ts'
import type { AppContext, PointerInfo } from './tools/types.ts'
import { recognizeShape } from './tools/beautify.ts'
import { inkPoints } from './canvas/render-shape.ts'
import { closePopover, h, toast } from './ui/dom.ts'
import { openContextMenu } from './ui/context-menu.ts'
import {
  openBackgroundDialog,
  openConfirmDialog,
  openLinkDialog,
  openNameDialog,
  openShareDialog,
  openShortcutsDialog,
  openTemplatesDialog,
} from './ui/dialogs.ts'
import { SelectionToolbar } from './ui/selection-toolbar.ts'
import { TextEditor } from './ui/text-editor.ts'
import { Toolbar } from './ui/toolbar.ts'
import { TopBar } from './ui/topbar.ts'
import { UndoBar, ZoomBar } from './ui/zoombar.ts'
import { TEMPLATES } from './ui/templates.ts'

export interface AppOptions {
  boardId: string
  userId: string
  userName: string
  readOnly: boolean
  /** tipo con el que el servidor creó el tablero (siembra el mapa al abrirlo) */
  kind?: BoardKind
  /** nombre que tiene el tablero en el servidor (valor inicial del título) */
  name?: string
  onExit: () => void
}

const CURSOR_THROTTLE_MS = 40

export class App implements AppContext {
  readonly boardId: string
  readonly store = new Store()
  readonly doc: Y.Doc
  readonly board: BoardDoc
  readonly renderer: Renderer
  readonly provider: WebsocketProvider
  readonly userId: string
  readonly el: HTMLElement

  private input: InputManager
  private topbar: TopBar
  private toolbar: Toolbar
  private zoombar: ZoomBar
  private undobar: UndoBar
  private selectionToolbar: SelectionToolbar
  private presentBar: PresentBar
  /** modo presentación: arrastra la vista de las demás pantallas */
  readonly presentation: Presentation
  private textEditor: TextEditor
  private clipboard: Shape[] = []
  private cursorFrame = 0
  private lastCursorSent = 0
  private lastCameraSent = 0
  private firstSyncDone = false
  private remoteRelayoutTimer: number | null = null
  private cameraTimer: number | null = null
  private pendingCursor: PointerInfo | null = null
  private thumbTimer: number | null = null
  private onExit: () => void
  private disposers: (() => void)[] = []
  private lastPointerWorld = { x: 0, y: 0 }
  private lassoActive = false
  /** modo "conectar": el próximo clic en un tema completa la relación */
  private pendingRelationFrom: ShapeId | null = null

  constructor(options: AppOptions) {
    this.boardId = options.boardId
    this.userId = options.userId
    this.onExit = options.onExit
    this.store.userName = options.userName
    this.store.readOnly = options.readOnly

    this.doc = new Y.Doc()
    this.board = new BoardDoc(this.doc)

    const scheme = location.protocol === 'https:' ? 'wss' : 'ws'
    this.provider = new WebsocketProvider(
      `${scheme}://${location.host}/sync?board=${encodeURIComponent(options.boardId)}`,
      this.doc,
    )

    const scene = h('canvas', { class: 'scene' })
    const overlay = h('canvas', { class: 'overlay' })
    const hitLayer = h('div', { class: 'hit-layer' })
    const board = h('div', { class: 'board' }, [scene, overlay, hitLayer])
    this.el = h('div', {}, [board])

    this.renderer = new Renderer(scene, overlay, this.store, this.board)
    setImageLoadedCallback(() => this.renderer.invalidate())

    this.textEditor = new TextEditor(this)
    board.append(this.textEditor.el)

    this.presentation = new Presentation({
      store: this.store,
      userId: this.userId,
      peers: () => this.peers(),
      publish: (field, value) => this.awareness.setLocalStateField(field, value),
      toast: (message) => this.toast(message),
      onChange: () => this.refreshPresence(),
    })
    this.disposers.push(() => this.presentation.dispose())

    this.input = new InputManager(this, hitLayer)
    // La herramienta restaurada de la sesión anterior debe llegar también a la
    // entrada: si no, la barra marcaba la pluma y el puntero seguía en
    // "seleccionar" (y en el iPad el lápiz escribía donde querías borrar).
    this.input.setTool(this.store.tool)
    this.topbar = new TopBar(this)
    this.toolbar = new Toolbar(this)
    this.zoombar = new ZoomBar(this)
    this.undobar = new UndoBar(this)
    this.selectionToolbar = new SelectionToolbar(this)
    this.presentBar = new PresentBar(this)

    this.el.append(
      this.topbar.el,
      this.toolbar.el,
      this.zoombar.el,
      this.undobar.el,
      this.selectionToolbar.el,
      this.presentBar.el,
    )

    if (options.readOnly) {
      this.toolbar.el.classList.add('hidden')
      this.undobar.el.classList.add('hidden')
    }

    this.setupAwareness()
    this.setupDocumentEvents()
    this.setupWindowEvents()
    // En el iPad nativo, el Apple Pencil lo captura la app; aquí solo se recibe el trazo.
    this.disposers.push(connectNativeBridge(this))
    this.renderer.invalidate()

    this.provider.on('status', (status) => this.topbar.setStatus(status))
    this.provider.on('synced', (synced) => {
      if (!synced) return
      // Solo la PRIMERA sincronización: al reconectar no se vuelve a sembrar
      // ni a saltar la cámara (antes cada reconexión re-encuadraba la vista).
      if (this.firstSyncDone) return
      this.firstSyncDone = true
      // Un mapa mental recién creado se siembra con su tema central. El id del
      // tema es fijo, así que dos pestañas que corran esto a la vez convergen.
      // - Fuera del historial (origen 'silent'): antes el primer Ctrl+Z del
      //   creador borraba el tema central de TODOS y volvía el mapa pizarra.
      // - Una sola vez en la vida del mapa (meta 'seeded'): tras "borrar todo",
      //   otro cliente que abría el mapa lo re-sembraba y el deshacer del que
      //   borró perdía el tema central original.
      // Un tablero recién creado aún no tiene título en el documento: se toma
      // el del servidor (antes un mapa abría como "Pizarra sin título").
      if (options.name && !this.board.meta.get('name') && !this.store.readOnly) {
        this.board.doc.transact(() => this.board.meta.set('name', options.name), 'silent')
      }
      if (options.kind === 'mindmap' && !this.store.readOnly) {
        this.board.doc.transact(() => {
          if (this.board.kind !== 'mindmap') this.board.setKind('mindmap')
          if (!this.board.meta.get('seeded')) {
            if (this.board.count() === 0) seedMindMap(this.board, this.getUserName(), 0, 0)
            this.board.meta.set('seeded', true)
          }
        }, 'silent')
      }
      this.topbar.syncTitle()
      // Precarga las imágenes del tablero para que no aparezcan "cargando".
      const sources = this.board
        .all()
        .filter((s): s is Extract<Shape, { type: 'image' }> => s.type === 'image')
        .map((s) => s.src)
      void preloadImages(sources).then(() => this.renderer.invalidate())
      if (this.board.count() > 0) this.zoomToFit()
      this.applyCanvasTheme()
    })
  }

  /* ── AppContext ─────────────────────────────────────────── */

  get awareness(): WebsocketProvider['awareness'] {
    return this.provider.awareness
  }

  getUserName(): string {
    return this.store.userName
  }

  shapes(): Shape[] {
    return this.board.all()
  }

  shapeAt(x: number, y: number): Shape | null {
    return hitTest(this.board.all(), x, y, this.store.camera.z)
  }

  toast(message: string): void {
    toast(message)
  }

  touch(): void {
    this.scheduleThumbnail()
  }

  editText(id: ShapeId, selectAll = false): void {
    if (this.store.readOnly) return
    this.textEditor.open(id, selectAll)
  }

  setLaser(points: number[] | null): void {
    this.awareness.setLocalStateField('laser', points)
  }

  setTool(tool: ToolId | 'lasso'): void {
    this.cancelPendingRelation()
    if (this.store.readOnly && tool !== 'hand' && tool !== 'select' && tool !== 'laser') return
    this.textEditor.close()
    closePopover()
    this.lassoActive = tool === 'lasso'
    this.input.setTool(tool)
    this.store.setTool(tool === 'lasso' ? 'select' : tool)
    this.awareness.setLocalStateField('tool', tool)
    this.input.updateCursor()
  }

  isLassoActive(): boolean {
    return this.lassoActive && this.store.tool === 'select'
  }

  onCursorMove(p: PointerInfo): void {
    this.lastPointerWorld = { x: p.x, y: p.y }
    this.pendingCursor = p
    // Limitado por tiempo (no por cuadro): así también se publica cuando la
    // pestaña está en segundo plano y requestAnimationFrame no corre.
    const now = performance.now()
    if (now - this.lastCursorSent < CURSOR_THROTTLE_MS) {
      if (this.cursorFrame === 0) {
        this.cursorFrame = window.setTimeout(() => {
          this.cursorFrame = 0
          this.publishCursor()
        }, CURSOR_THROTTLE_MS)
      }
      return
    }
    this.publishCursor()
  }

  private publishCursor(): void {
    const point = this.pendingCursor
    if (!point) return
    this.lastCursorSent = performance.now()
    this.awareness.setLocalStateField('cursor', {
      x: Math.round(point.x * 10) / 10,
      y: Math.round(point.y * 10) / 10,
    })
  }

  /* ── Mapa mental ────────────────────────────────────────── */

  get isMindMap(): boolean {
    return this.board.kind === 'mindmap'
  }

  /** Crea un hijo del tema dado, lo selecciona y abre su editor. */
  mindmapAddChild(parentId: ShapeId): void {
    if (this.store.readOnly) return
    const child = addChild(this.board, this.getUserName(), parentId)
    if (!child) return
    this.store.setSelection([child.id])
    this.editText(child.id, true)
    this.touch()
  }

  /** Crea un hermano del tema dado, lo selecciona y abre su editor. */
  mindmapAddSibling(id: ShapeId): void {
    if (this.store.readOnly) return
    const sibling = addSibling(this.board, this.getUserName(), id)
    if (!sibling) return
    this.store.setSelection([sibling.id])
    this.editText(sibling.id, true)
    this.touch()
  }

  mindmapToggleCollapse(id: ShapeId): void {
    if (this.store.readOnly) return
    toggleCollapse(this.board, id)
    this.touch()
  }

  /** Arranca el modo conectar: el próximo clic en otro tema crea la relación. */
  mindmapStartRelation(from: ShapeId): void {
    if (this.store.readOnly) return
    this.pendingRelationFrom = from
    this.toast('Toca el otro tema para conectarlos')
  }

  /** Cancela el modo conectar (Escape, cambio de herramienta, clic en vacío). */
  cancelPendingRelation(): void {
    this.pendingRelationFrom = null
  }

  get hasPendingRelation(): boolean {
    return this.pendingRelationFrom !== null
  }

  /** El clic aterrizó en una figura con el modo conectar activo. */
  maybeCompleteRelation(targetId: ShapeId): boolean {
    const from = this.pendingRelationFrom
    if (!from) return false
    this.pendingRelationFrom = null
    const target = this.board.get(targetId)
    if (target?.type !== 'topic' || from === targetId) {
      this.toast('La conexión se canceló')
      return true
    }
    addRelation(this.board, this.getUserName(), from, targetId)
    this.touch()
    return true
  }

  /** Navegación con flechas entre temas: el más cercano en esa dirección. */
  private mindmapNavigate(dx: number, dy: number): void {
    const currentId = this.store.selection[0]
    const current = currentId ? this.board.get(currentId) : null
    if (current?.type !== 'topic') return
    const cx = current.x + current.w / 2
    const cy = current.y + current.h / 2
    let best: { id: ShapeId; score: number } | null = null
    for (const shape of this.shapes()) {
      if (shape.type !== 'topic' || shape.id === currentId || shape.hidden) continue
      const ox = shape.x + shape.w / 2 - cx
      const oy = shape.y + shape.h / 2 - cy
      // Debe estar del lado pedido y preferir lo alineado con el eje.
      const along = ox * dx + oy * dy
      if (along <= 0) continue
      const across = Math.abs(ox * dy) + Math.abs(oy * dx)
      const score = along + across * 2.5
      if (!best || score < best.score) best = { id: shape.id, score }
    }
    if (best) this.store.setSelection([best.id])
  }

  /* ── Presencia y seguimiento ────────────────────────────── */

  private setupAwareness(): void {
    this.awareness.setLocalState({
      id: this.userId,
      name: this.store.userName,
      color: presenceColorFor(this.userId),
      cursor: null,
      selection: [],
      camera: this.store.camera,
      tool: this.store.tool,
      presenting: false,
    })

    this.renderer.setPresenceProvider(() => (this.store.showCursors ? this.peers() : []))

    const onAwareness = (): void => {
      this.refreshPresence()
      // Engancharse o soltarse de quien presenta, y perseguir su cámara.
      this.presentation.sync()
    }
    this.awareness.on('change', onAwareness)
    this.disposers.push(() => this.awareness.off('change', onAwareness))
    onAwareness()

    const unsubscribe = this.store.subscribe(() => {
      this.applyCanvasTheme()
      this.awareness.setLocalStateField('selection', this.store.selection)
      this.publishCamera()
      this.renderer.invalidate()
      this.textEditor.reposition()
    })
    this.disposers.push(unsubscribe)
  }

  private selfPresence(): Presence {
    return {
      id: this.userId,
      name: this.store.userName,
      color: presenceColorFor(this.userId),
      cursor: null,
    }
  }

  peers(): Presence[] {
    const out: Presence[] = []
    for (const [clientId, state] of this.awareness.getStates()) {
      if (clientId === this.doc.clientID) continue
      const presence = state as unknown as Presence
      if (!presence?.id) continue
      out.push(presence)
    }
    return out
  }

  toggleFollow(userId: string): void {
    this.presentation.toggleManualFollow(userId)
    this.store.emit()
  }

  /** El usuario movió la vista con su mano: se suelta del que presenta. */
  onUserCameraMove(): void {
    this.presentation.onUserCameraMove()
  }

  /** Reconciliación diferida del mapa tras cambios remotos o un deshacer. */
  private scheduleRemoteRelayout(): void {
    if (this.remoteRelayoutTimer !== null) return
    this.remoteRelayoutTimer = window.setTimeout(() => {
      this.remoteRelayoutTimer = null
      if (this.store.readOnly) return
      // Nunca a mitad de un arrastre: devolvía el tema a su hueco mientras el
      // usuario apuntaba al destino. Se vuelve a intentar al terminar.
      if (this.input.isBusy) {
        this.scheduleRemoteRelayout()
        return
      }
      // Origen 'silent': acomodar lo que llegó de otros no es una acción tuya
      // y no debe aparecer en tu historial de deshacer.
      this.board.doc.transact(() => relayout(this.board), 'silent')
    }, 350)
  }

  /** Repinta lo que depende de quién está conectado. */
  private refreshPresence(): void {
    this.topbar.renderPresence(this.peers(), this.selfPresence())
    this.presentBar.render()
    this.renderer.invalidateOverlay()
  }

  /**
   * La cámara se publica como mucho 25 veces por segundo: al arrastrar salían
   * más de cien avisos por segundo y el que sigue no gana nada, porque
   * interpola entre uno y otro.
   */
  private publishCamera(): void {
    const now = performance.now()
    if (now - this.lastCameraSent >= 40) {
      this.lastCameraSent = now
      this.awareness.setLocalStateField('camera', this.store.camera)
      return
    }
    if (this.cameraTimer !== null) return
    this.cameraTimer = window.setTimeout(() => {
      this.cameraTimer = null
      this.lastCameraSent = performance.now()
      this.awareness.setLocalStateField('camera', this.store.camera)
    }, 40)
  }

  /* ── Documento ──────────────────────────────────────────── */

  private setupDocumentEvents(): void {
    this.disposers.push(
      this.board.onChange((ids) => {
        for (const id of ids) forgetShapeCache(id)
        this.renderer.invalidate()
        this.scheduleThumbnail()
        // El editor flotante sigue al tema si un colaborador reacomodó el mapa.
        this.textEditor.reposition()
        // Cambios de temas llegados de OTROS clientes (o de un deshacer) pueden
        // dejar el mapa a medio acomodar (hijos encimados, ocultos huérfanos,
        // ciclos por recolgados cruzados). Un relayout diferido lo reconcilia:
        // con las mismas entradas todos computan lo mismo, y si nada cambió no
        // se escribe nada, así que no hay tormenta de actualizaciones.
        if (!this.store.readOnly) {
          let hayTemas = false
          for (const id of ids) {
            const shape = this.board.get(id)
            if (shape?.type === 'topic' || shape === undefined) {
              hayTemas = true
              break
            }
          }
          if (hayTemas) this.scheduleRemoteRelayout()
        }
        // Descarga imágenes nuevas.
        for (const id of ids) {
          const shape = this.board.get(id)
          if (shape?.type === 'image') void preloadImages([shape.src]).then(() => this.renderer.invalidate())
        }
      }),
    )
    this.disposers.push(
      this.board.onMetaChange(() => {
        this.applyCanvasTheme()
        this.renderer.invalidate()
      }),
    )
  }

  private applyCanvasTheme(): void {
    const fondoOscuro = this.board.background === 'dark' || this.board.background === 'blueprint'
    document.body.classList.toggle('canvas-dark', fondoOscuro || this.store.darkMode)
    document.body.classList.toggle('theme-dark', this.store.darkMode)
  }

  /* ── Eventos globales ───────────────────────────────────── */

  private setupWindowEvents(): void {
    const onPaste = (e: ClipboardEvent): void => {
      if (isEditingField(e.target) || this.store.readOnly) return
      const items = e.clipboardData?.items
      if (!items) return
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile()
          if (file) {
            e.preventDefault()
            void this.insertImageFile(file, this.lastPointerWorld.x, this.lastPointerWorld.y)
            return
          }
        }
      }
      const text = e.clipboardData?.getData('text/plain')
      if (text && this.clipboard.length === 0) {
        e.preventDefault()
        this.insertTextAt(this.lastPointerWorld.x, this.lastPointerWorld.y, text)
      } else if (this.clipboard.length > 0) {
        e.preventDefault()
        void this.pasteAt(this.lastPointerWorld.x, this.lastPointerWorld.y)
      }
    }
    const onDrop = (e: DragEvent): void => {
      e.preventDefault()
      if (this.store.readOnly) return
      const files = Array.from(e.dataTransfer?.files ?? []).filter((f) => f.type.startsWith('image/'))
      if (files.length === 0) return
      const world = this.renderer.screenToWorld(e.clientX, e.clientY)
      files.forEach((file, i) => void this.insertImageFile(file, world.x + i * 30, world.y + i * 30))
    }
    const onDragOver = (e: DragEvent): void => e.preventDefault()
    const onBeforeUnload = (): void => {
      this.flushThumbnail()
    }

    window.addEventListener('paste', onPaste)
    window.addEventListener('drop', onDrop)
    window.addEventListener('dragover', onDragOver)
    window.addEventListener('beforeunload', onBeforeUnload)
    this.disposers.push(() => {
      window.removeEventListener('paste', onPaste)
      window.removeEventListener('drop', onDrop)
      window.removeEventListener('dragover', onDragOver)
      window.removeEventListener('beforeunload', onBeforeUnload)
    })
  }

  onKeyDown(e: KeyboardEvent): void {
    // Solo lectura: nada de teclas que muten. Navegar y salir sí.
    if (this.store.readOnly) {
      if (e.key === 'Escape') {
        this.store.setSelection([])
        closePopover()
      }
      return
    }
    if (isEditingField(e.target)) return
    const mod = e.metaKey || e.ctrlKey

    if (e.altKey && !mod) {
      const map: Record<string, ToolId | 'lasso'> = {
        s: 'select',
        q: 'lasso',
        w: 'draw',
        h: 'highlighter',
        x: 'eraser',
        l: 'laser',
      }
      const tool = map[e.key.toLowerCase()]
      if (tool) {
        e.preventDefault()
        this.setTool(tool)
        return
      }
      if (e.key.toLowerCase() === 'b') {
        e.preventDefault()
        this.beautifySelection()
        return
      }
      if (e.key === '+' || e.key === '=') {
        e.preventDefault()
        this.zoomBy(1.2)
        return
      }
      if (e.key === '-') {
        e.preventDefault()
        this.zoomBy(1 / 1.2)
        return
      }
    }

    if (mod) {
      switch (e.key.toLowerCase()) {
        case 'z':
          e.preventDefault()
          if (e.shiftKey) this.redo()
          else this.undo()
          return
        case 'y':
          e.preventDefault()
          this.redo()
          return
        case 'a':
          e.preventDefault()
          this.selectAll()
          return
        case 'c':
          this.copySelection()
          return
        case 'x': {
          // Los temas del mapa no van al portapapeles (se mueven arrastrando o
          // se duplican con Ctrl+D): cortar solo borra lo que sí se copió.
          const temas = this.store.selection.filter((id) => {
            const t = this.board.get(id)?.type
            return t === 'topic' || t === 'relation'
          })
          if (temas.length > 0) {
            this.toast('Los temas se mueven arrastrándolos, o se duplican con Ctrl+D')
            return
          }
          this.copySelection()
          this.deleteSelection()
          return
        }
        case 'd':
          e.preventDefault()
          this.duplicateSelection()
          return
        case ']':
          if (e.shiftKey) {
            e.preventDefault()
            this.bringToFront()
          }
          return
        case '[':
          if (e.shiftKey) {
            e.preventDefault()
            this.sendToBack()
          }
          return
        default:
          return
      }
    }

    switch (e.key) {
      case 'Escape':
        this.cancelPendingRelation()
        this.store.setSelection([])
        this.textEditor.close()
        closePopover()
        if (this.store.followingUser) {
          this.presentation.leave()
          this.store.emit()
        }
        break
      case 'Tab': {
        // Con el foco en un botón o diálogo, Tab es navegación por teclado.
        if (document.activeElement && document.activeElement !== document.body) break
        // Tab sobre un tema: hijo nuevo (flujo XMind).
        const seleccionado = this.store.selection.length === 1 ? this.board.get(this.store.selection[0]) : null
        if (seleccionado?.type === 'topic') {
          e.preventDefault()
          this.mindmapAddChild(seleccionado.id)
        }
        break
      }
      case 'Delete':
      case 'Backspace':
        if (this.store.selection.length > 0) {
          e.preventDefault()
          this.deleteSelection()
        }
        break
      case 'Enter': {
        if (this.store.selection.length !== 1) break
        // Con el foco en un botón, Enter es "apretar el botón", no un tema nuevo.
        if (document.activeElement && document.activeElement !== document.body) break
        e.preventDefault()
        const figura = this.board.get(this.store.selection[0])
        // Enter sobre un tema: hermano nuevo (y sobre el central, un hijo).
        if (figura?.type === 'topic') this.mindmapAddSibling(figura.id)
        else this.editText(this.store.selection[0], true)
        break
      }
      case 'F2':
        if (this.store.selection.length === 1) {
          e.preventDefault()
          this.editText(this.store.selection[0], true)
        }
        break
      case 'ArrowUp':
      case 'ArrowDown':
      case 'ArrowLeft':
      case 'ArrowRight': {
        e.preventDefault()
        const dxDir = e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowRight' ? 1 : 0
        const dyDir = e.key === 'ArrowUp' ? -1 : e.key === 'ArrowDown' ? 1 : 0
        // Sobre un tema, las flechas NAVEGAN el mapa (su posición la pone el layout).
        const activo = this.store.selection.length === 1 ? this.board.get(this.store.selection[0]) : null
        if (activo?.type === 'topic') {
          this.mindmapNavigate(dxDir, dyDir)
          break
        }
        const step = e.shiftKey ? 20 : 2
        if (this.store.selection.length > 0) this.nudgeSelection(dxDir * step, dyDir * step)
        else this.store.setCamera({ x: this.store.camera.x + dxDir * step * 8, y: this.store.camera.y + dyDir * step * 8 })
        break
      }
      case 'n':
      case 'N':
        this.setTool('note')
        break
      case 't':
      case 'T':
        this.setTool('text')
        break
      case 'r':
      case 'R':
        this.setTool('shape')
        break
      case 'v':
      case 'V':
        this.setTool('select')
        break
      case 'h':
      case 'H':
        // La mano: mover el lienzo. (Alt+H sigue siendo el resaltador.)
        this.setTool('hand')
        break
      case 'p':
      case 'P':
        this.setTool('draw')
        break
      case 'e':
      case 'E':
        this.setTool('eraser')
        break
      case '1':
        if (e.shiftKey) this.zoomToFit()
        break
      case '0':
        if (e.shiftKey) this.zoomTo(1)
        break
      default:
        break
    }
  }

  openContextMenu(sx: number, sy: number, clientX: number, clientY: number): void {
    if (this.store.readOnly) return
    openContextMenu(this, sx, sy, clientX, clientY)
  }

  /* ── Acciones de edición ────────────────────────────────── */

  updateSelection(patch: (shape: Shape) => Partial<Shape>): void {
    const ids = this.store.selection
    if (ids.length === 0) return
    this.board.transact(() => {
      for (const id of ids) {
        const shape = this.board.get(id)
        if (!shape) continue
        this.board.shapes.set(id, { ...shape, ...patch(shape), updatedAt: Date.now() } as Shape)
      }
    })
    this.renderer.invalidate()
    this.touch()
  }

  nudgeSelection(dx: number, dy: number): void {
    if (this.store.readOnly) return
    this.updateSelection((shape) => ({ x: shape.x + dx, y: shape.y + dy }))
  }

  deleteSelection(): void {
    if (this.store.readOnly) return
    const ids = this.store.selection.filter((id) => !this.board.get(id)?.locked)
    if (ids.length === 0) return
    // Un tema del mapa se lleva su subárbol entero (y sus relaciones).
    const temas = ids.filter((id) => this.board.get(id)?.type === 'topic')
    const resto = ids.filter((id) => this.board.get(id)?.type !== 'topic')
    for (const id of temas) deleteSubtree(this.board, id)
    if (resto.length > 0) this.board.delete(resto)
    this.store.setSelection([])
    this.touch()
  }

  duplicateSelection(): void {
    if (this.store.readOnly) return
    const shapes = this.store.selection.map((id) => this.board.get(id)).filter(Boolean) as Shape[]
    if (shapes.length === 0) return
    // Un tema se duplica con TODO su subárbol, como árbol flotante.
    if (shapes.length === 1 && shapes[0].type === 'topic') {
      const copia = duplicateSubtree(this.board, this.getUserName(), shapes[0].id)
      if (copia) this.store.setSelection([copia.id])
      this.touch()
      return
    }
    const sinMapa = shapes.filter((s) => s.type !== 'topic' && s.type !== 'relation')
    if (sinMapa.length === 0) return
    const copies = duplicateShapes(sinMapa, 24, 24, this.board.maxZ())
    this.board.putMany(copies)
    this.store.setSelection(copies.map((s) => s.id))
    this.touch()
  }

  copySelection(): void {
    // Los temas del mapa no van al portapapeles: un clon suelto con el mismo
    // padre y el mismo orden corrompe el árbol. Se duplican con Ctrl+D.
    const shapes = (this.store.selection.map((id) => this.board.get(id)).filter(Boolean) as Shape[]).filter(
      (s) => s.type !== 'topic' && s.type !== 'relation',
    )
    if (shapes.length === 0) return
    this.clipboard = shapes.map((s) => ({ ...s }))
    void navigator.clipboard?.writeText(shapes.map((s) => shapeToText(s)).filter(Boolean).join('\n')).catch(() => {})
    this.toast(`${shapes.length} elemento${shapes.length === 1 ? '' : 's'} copiado${shapes.length === 1 ? '' : 's'}`)
  }

  async pasteAt(x: number, y: number): Promise<void> {
    if (this.clipboard.length === 0) return
    let minX = Infinity
    let minY = Infinity
    for (const shape of this.clipboard) {
      minX = Math.min(minX, shape.x)
      minY = Math.min(minY, shape.y)
    }
    const copies = duplicateShapes(this.clipboard, x - minX, y - minY, this.board.maxZ())
    this.board.putMany(copies)
    this.store.setSelection(copies.map((s) => s.id))
    this.touch()
  }

  selectAll(): void {
    this.setTool('select')
    this.store.setSelection(this.board.all().filter((s) => !s.locked).map((s) => s.id))
  }

  bringToFront(): void {
    this.board.bringToFront(this.store.selection)
    this.touch()
  }

  sendToBack(): void {
    this.board.sendToBack(this.store.selection)
    this.touch()
  }

  toggleLock(): void {
    const shapes = this.store.selection.map((id) => this.board.get(id)).filter(Boolean) as Shape[]
    if (shapes.length === 0) return
    const lock = !shapes.every((s) => s.locked)
    this.updateSelection(() => ({ locked: lock }))
    if (lock) this.store.setSelection([])
    this.toast(lock ? 'Elementos bloqueados' : 'Elementos desbloqueados')
  }

  unlockAll(): void {
    const locked = this.board.all().filter((s) => s.locked)
    if (locked.length === 0) return
    this.board.putMany(locked.map((s) => ({ ...s, locked: false })))
    this.toast('Todo desbloqueado')
  }

  beautifySelection(): void {
    const shapes = this.store.selection.map((id) => this.board.get(id)).filter(Boolean) as Shape[]
    const drawings = shapes.filter((s) => s.type === 'draw' && !s.highlighter)
    if (drawings.length === 0) {
      this.store.setPref('enhanceShapes', !this.store.enhanceShapes)
      this.toast(this.store.enhanceShapes ? 'Mejorar formas: activado' : 'Mejorar formas: desactivado')
      return
    }
    const created: Shape[] = []
    const removed: string[] = []
    for (const shape of drawings) {
      if (shape.type !== 'draw') continue
      const pts = inkPoints(shape).map((p) => ({ x: p.x + shape.x, y: p.y + shape.y, p: p.p }))
      const geo = recognizeShape(pts, {
        color: shape.color,
        strokeWidth: Math.max(2, shape.size * 0.8),
        createdBy: this.userId,
        z: shape.z,
      })
      if (geo) {
        created.push(geo)
        removed.push(shape.id)
      }
    }
    if (created.length === 0) {
      this.toast('No se reconoció ninguna forma')
      return
    }
    this.board.transact(() => {
      for (const id of removed) this.board.shapes.delete(id)
      for (const shape of created) this.board.shapes.set(shape.id, shape)
    })
    this.store.setSelection(created.map((s) => s.id))
    this.toast(`${created.length} forma${created.length === 1 ? '' : 's'} mejorada${created.length === 1 ? '' : 's'}`)
  }

  clearBoard(): void {
    openConfirmDialog({
      title: 'Borrar todo el tablero',
      message:
        'Se eliminarán todos los elementos de esta pizarra para todos los participantes. Si te arrepientes, Ctrl+Z (⌘Z) lo deshace.',
      confirmLabel: 'Borrar todo',
      onConfirm: () => {
        this.board.delete(this.board.all().map((s) => s.id))
        this.store.setSelection([])
        this.touch()
      },
    })
  }

  undo(): void {
    this.textEditor.close()
    this.board.undo()
    this.renderer.invalidate()
  }

  redo(): void {
    this.textEditor.close()
    this.board.redo()
    this.renderer.invalidate()
  }

  /* ── Inserciones ────────────────────────────────────────── */

  viewCenterWorld(): { x: number; y: number } {
    return this.viewCenter()
  }

  private viewCenter(): { x: number; y: number } {
    const { width, height } = this.renderer.viewportSize
    return this.renderer.screenToWorld(width / 2, height / 2)
  }

  insertNoteAt(x: number, y: number): void {
    const note = createNote({
      x: x - 100,
      y: y - 100,
      color: this.store.style.noteColor,
      createdBy: this.userId,
      author: this.store.userName,
      z: this.board.maxZ() + 1,
    })
    this.board.put(note)
    this.setTool('select')
    this.store.setSelection([note.id])
    this.editText(note.id)
  }

  insertTextAt(x: number, y: number, text = ''): void {
    const shape = createText({
      x,
      y,
      color: this.store.style.textColor,
      fontSize: this.store.style.textSize,
      createdBy: this.userId,
      z: this.board.maxZ() + 1,
    })
    if (text) {
      shape.text = text
      shape.h = Math.max(shape.h, Math.ceil(text.length / 40) * shape.fontSize * 1.25)
    }
    this.board.put(shape)
    this.setTool('select')
    this.store.setSelection([shape.id])
    if (!text) this.editText(shape.id)
    this.touch()
  }

  insertNoteGrid(): void {
    const center = this.viewCenter()
    const colors = ['#FEF3A3', '#C9E7FF', '#D6F0BE', '#FFD1E3']
    const size = 160
    const gap = 20
    const cols = 4
    const rows = 3
    const originX = center.x - ((size + gap) * cols) / 2
    const originY = center.y - ((size + gap) * rows) / 2
    const notes: Shape[] = []
    let z = this.board.maxZ()
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        notes.push(
          createNote({
            x: originX + col * (size + gap),
            y: originY + row * (size + gap),
            color: colors[col % colors.length],
            createdBy: this.userId,
            author: this.store.userName,
            z: ++z,
            size,
          }),
        )
      }
    }
    this.board.putMany(notes)
    this.setTool('select')
    this.store.setSelection(notes.map((n) => n.id))
    this.touch()
  }

  insertReaction(emoji: string): void {
    const center = this.viewCenter()
    const shape = createText({
      x: center.x - 30,
      y: center.y - 30,
      color: '#1A1A1A',
      fontSize: 64,
      createdBy: this.userId,
      z: this.board.maxZ() + 1,
    })
    shape.text = emoji
    shape.w = 80
    shape.h = 84
    shape.align = 'center'
    this.board.put(shape)
    this.setTool('select')
    this.store.setSelection([shape.id])
    this.touch()
  }

  insertLink(): void {
    openLinkDialog((url, label) => {
      const center = this.viewCenter()
      const shape = createText({
        x: center.x - 160,
        y: center.y - 16,
        color: '#0F6CBD',
        fontSize: 24,
        createdBy: this.userId,
        z: this.board.maxZ() + 1,
      })
      shape.text = `🔗 ${label}\n${url}`
      shape.w = 420
      shape.h = 80
      this.board.put(shape)
      this.touch()
    })
  }

  insertTemplate(id: string): void {
    const template = TEMPLATES.find((t) => t.id === id)
    if (!template) return
    const center = this.viewCenter()
    const shapes = template.build(center.x - 400, center.y - 260, this.userId, this.board.maxZ() + 1)
    this.board.putMany(shapes)
    this.setTool('select')
    this.store.setSelection(shapes.map((s) => s.id))
    this.touch()
    setTimeout(() => this.zoomToSelection(), 40)
  }

  pickImage(): void {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.multiple = true
    input.addEventListener('change', () => {
      const files = Array.from(input.files ?? [])
      const center = this.viewCenter()
      files.forEach((file, i) => void this.insertImageFile(file, center.x + i * 30, center.y + i * 30))
    })
    input.click()
  }

  async insertImageFile(file: File | Blob, x: number, y: number): Promise<void> {
    try {
      this.toast('Subiendo imagen…')
      const name = 'name' in file ? file.name : 'imagen.png'
      const { url, width, height } = await api.upload(file, name, this.boardId)
      const maxSide = 520
      const scale = Math.min(1, maxSide / Math.max(width, height))
      const w = Math.round(width * scale)
      const h = Math.round(height * scale)
      const shape = createImage({
        src: url,
        x: x - w / 2,
        y: y - h / 2,
        w,
        h,
        naturalW: width,
        naturalH: height,
        createdBy: this.userId,
        z: this.board.maxZ() + 1,
      })
      this.board.put(shape)
      this.setTool('select')
      this.store.setSelection([shape.id])
      this.touch()
    } catch (error) {
      // El motivo real del servidor ("demasiado grande", "no se aceptan SVG")
      // le sirve más a quien sube que un aviso genérico.
      const motivo = error instanceof Error && error.message ? `: ${error.message}` : ''
      this.toast(`No se pudo subir la imagen${motivo}`)
    }
  }

  /* ── Cámara ─────────────────────────────────────────────── */

  zoomBy(factor: number): void {
    const { width, height } = this.renderer.viewportSize
    const center = this.renderer.screenToWorld(width / 2, height / 2)
    this.zoomTo(this.store.camera.z * factor, center.x, center.y)
  }

  zoomTo(z: number, worldX?: number, worldY?: number): void {
    const { width, height } = this.renderer.viewportSize
    const target = clamp(z, MIN_ZOOM, MAX_ZOOM)
    const center = worldX !== undefined && worldY !== undefined
      ? { x: worldX, y: worldY }
      : this.renderer.screenToWorld(width / 2, height / 2)
    this.store.setCamera({
      z: target,
      x: center.x - width / 2 / target,
      y: center.y - height / 2 / target,
    })
    this.presentation.onUserCameraMove()
  }

  zoomToFit(): void {
    const bounds = this.contentBounds()
    if (!bounds) {
      this.zoomTo(1, 0, 0)
      return
    }
    this.zoomToBox(bounds, 80)
  }

  zoomToSelection(): void {
    const bounds = this.renderer.selectionBounds()
    if (!bounds) return this.zoomToFit()
    this.zoomToBox(bounds, 120, 1)
  }

  private zoomToBox(box: Box, padding: number, maxZoom = MAX_ZOOM): void {
    const { width, height } = this.renderer.viewportSize
    // El lienzo puede no tener tamaño todavía (primer cuadro tras montarse).
    if (width < padding * 2 + 10 || height < padding * 2 + 10) {
      requestAnimationFrame(() => this.zoomToBox(box, padding, maxZoom))
      return
    }
    const z = clamp(
      Math.min((width - padding * 2) / Math.max(box.w, 1), (height - padding * 2) / Math.max(box.h, 1)),
      MIN_ZOOM,
      Math.min(maxZoom, MAX_ZOOM),
    )
    this.store.setCamera({
      z,
      x: box.x + box.w / 2 - width / 2 / z,
      y: box.y + box.h / 2 - height / 2 / z,
    })
    this.presentation.onUserCameraMove()
  }

  contentBounds(): Box | null {
    const shapes = this.board.all()
    if (shapes.length === 0) return null
    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity
    for (const shape of shapes) {
      const b = shapeBounds(shape)
      minX = Math.min(minX, b.x)
      minY = Math.min(minY, b.y)
      maxX = Math.max(maxX, b.x + b.w)
      maxY = Math.max(maxY, b.y + b.h)
    }
    return { x: minX, y: minY, w: maxX - minX, h: maxY - minY }
  }

  /* ── Diálogos ───────────────────────────────────────────── */

  openShare(): void {
    openShareDialog(this)
  }

  openBackgroundPicker(): void {
    openBackgroundDialog(this)
  }

  openTemplates(): void {
    openTemplatesDialog(this)
  }

  openShortcuts(): void {
    openShortcutsDialog()
  }

  changeName(): void {
    openNameDialog(this.store.userName, (name) => {
      this.store.userName = name
      localStorage.setItem('wb.name', name)
      this.awareness.setLocalStateField('name', name)
      void api.setName(name).catch(() => {})
      this.store.emit()
    })
  }

  setBackground(bg: BackgroundId): void {
    this.board.setBackground(bg)
    this.applyCanvasTheme()
    this.renderer.invalidate()
  }

  /** Enlace de invitación con el rol pedido. Falla con error: sin respaldo sin token. */
  async createShareLink(role: 'edit' | 'view'): Promise<string> {
    const { url } = await api.createShare(this.boardId, role)
    return url
  }

  renameBoard(name: string): void {
    void api.renameBoard(this.boardId, name).catch(() => {
      this.toast('No se pudo guardar el nombre en la galería; se reintentará al renombrar de nuevo')
    })
  }

  goHome(): void {
    this.flushThumbnail()
    this.onExit()
  }

  /* ── Exportar e imagen en miniatura ─────────────────────── */

  async exportImage(): Promise<void> {
    const bounds = this.store.selection.length > 0 ? this.renderer.selectionBounds() : this.contentBounds()
    if (!bounds) {
      this.toast('No hay nada que exportar')
      return
    }
    const padding = 40
    const box = {
      x: bounds.x - padding,
      y: bounds.y - padding,
      w: bounds.w + padding * 2,
      h: bounds.h + padding * 2,
    }
    const scale = Math.min(2, 4000 / Math.max(box.w, box.h))
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(box.w * scale))
    canvas.height = Math.max(1, Math.round(box.h * scale))
    const ctx = canvas.getContext('2d')!
    await preloadImages(
      this.board.all().filter((s): s is Extract<Shape, { type: 'image' }> => s.type === 'image').map((s) => s.src),
    )
    this.renderer.renderTo(ctx, box, scale, true)
    canvas.toBlob((blob) => {
      if (!blob) return
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${this.board.name.replace(/[^\w\-. ]+/g, '') || 'pizarra'}.png`
      a.click()
      setTimeout(() => URL.revokeObjectURL(url), 2000)
      this.toast('Imagen exportada')
    }, 'image/png')
  }

  private scheduleThumbnail(): void {
    if (this.thumbTimer !== null) clearTimeout(this.thumbTimer)
    this.thumbTimer = window.setTimeout(() => {
      this.thumbTimer = null
      void this.saveThumbnail()
    }, 4000)
  }

  private flushThumbnail(): void {
    if (this.thumbTimer !== null) {
      clearTimeout(this.thumbTimer)
      this.thumbTimer = null
      void this.saveThumbnail()
    }
  }

  private async saveThumbnail(): Promise<void> {
    if (this.store.readOnly) return
    const bounds = this.contentBounds()
    if (!bounds) return
    const width = 440
    const height = 260
    const scale = Math.min(width / Math.max(bounds.w, 1), height / Math.max(bounds.h, 1), 1)
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')!
    const box = {
      x: bounds.x + bounds.w / 2 - width / 2 / scale,
      y: bounds.y + bounds.h / 2 - height / 2 / scale,
      w: width / scale,
      h: height / scale,
    }
    this.renderer.renderTo(ctx, box, scale, true)
    try {
      await api.saveThumb(this.boardId, canvas.toDataURL('image/jpeg', 0.55))
    } catch {
      /* la miniatura es opcional */
    }
  }

  destroy(): void {
    this.flushThumbnail()
    if (this.cursorFrame) clearTimeout(this.cursorFrame)
    for (const dispose of this.disposers) dispose()
    this.input.destroy()
    this.renderer.destroy()
    this.provider.destroy()
    this.doc.destroy()
    this.el.remove()
    closePopover()
  }
}

function shapeToText(shape: Shape): string {
  if (shape.type === 'note' || shape.type === 'text') return shape.text
  if (shape.type === 'shape') return shape.text ?? ''
  return ''
}

export { newId }